import type { SimpleOption } from "@/utils/form/constants"
import { forwardRef } from "@chakra-ui/react"
import {
  type DropdownIndicatorProps,
  type GroupBase,
  type Props,
  Select as ReactSelect,
  type SelectInstance,
  chakraComponents,
} from "chakra-react-select"
import type { RefAttributes } from "react"
import { type Control, Controller, type RefCallBack, useForm } from "react-hook-form"
import { useIntl } from "react-intl"
import { DropdownIcon } from "../Icons"
import { getChakraStyles } from "./chakraStyles"

type SelectProps = Omit<
  Props<SimpleOption, false, GroupBase<SimpleOption>> &
    RefAttributes<SelectInstance<SimpleOption, false, GroupBase<SimpleOption>>> & {
      control: unknown
      placeholder?: string
      selectedValue?: string
      onSelect?: (newValue: string) => void
      options?: SimpleOption[]
      hasAll?: boolean
      disabledValues?: string[]
      ref?: RefCallBack | React.RefObject<SelectType>
    },
  "onChange"
>

export type SelectType = HTMLSelectElement &
  SelectInstance<SimpleOption, false, GroupBase<SimpleOption>>

export const Select: React.FC<SelectProps> = forwardRef(
  (
    { placeholder, selectedValue, onSelect, options, hasAll, control, disabledValues, ...props },
    ref
  ) => {
    const intl = useIntl()
    const validOptions: SimpleOption[] = options ?? []

    const { control: defaultControl } = useForm({
      defaultValues: { [props.name ?? "selectedValue"]: "" },
    })
    const ct = control ?? defaultControl

    const styleVariant = props.variant ?? "default"

    const dropDown = (
      dropdownIndicatorProps: DropdownIndicatorProps<SimpleOption, false, GroupBase<SimpleOption>>
    ) => (
      <chakraComponents.DropdownIndicator {...dropdownIndicatorProps}>
        <DropdownIcon color="text" />
      </chakraComponents.DropdownIndicator>
    )

    return (
      <Controller
        control={ct as Control}
        name={props.name ?? "selectedValue"}
        render={({ field: { onChange } }) => (
          <ReactSelect
            {...props}
            ref={ref}
            placeholder={placeholder ?? intl.formatMessage({ id: "search.defaultLabel" })}
            loadingMessage={() => <>{intl.formatMessage({ id: "search.loadingMessage" })}</>}
            defaultValue={validOptions.filter(option => option.value === (selectedValue ?? ""))}
            options={validOptions}
            onChange={newVal => {
              onChange(newVal?.value)
              onSelect?.(newVal?.value ?? "")
            }}
            components={{
              DropdownIndicator: dropDown,
            }}
            chakraStyles={{
              ...getChakraStyles(styleVariant),
              ...props.chakraStyles,
            }}
            size="sm"
          />
        )}
      />
    )
  }
)
